import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar, Filter, Plus, X } from "lucide-react";

import { Button } from "../ui/button";
import { Calendar as CalendarComponent } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { cn } from "@/lib/utils";

export type UserSessionsFilterType = "all" | "day" | "month" | "period";

type UserSessionsFiltersBarProps = {
  filterType: UserSessionsFilterType;
  onFilterTypeChange: (value: UserSessionsFilterType) => void;
  selectedDay: Date | undefined;
  onSelectedDayChange: (day: Date | undefined) => void;
  dayPopoverOpen: boolean;
  onDayPopoverOpenChange: (open: boolean) => void;
  selectedMonth: Date;
  onSelectedMonthChange: (month: Date) => void;
  monthPopoverOpen: boolean;
  onMonthPopoverOpenChange: (open: boolean) => void;
  periodStart: Date | undefined;
  periodEnd: Date | undefined;
  onPeriodStartChange: (day: Date | undefined) => void;
  onPeriodEndChange: (day: Date | undefined) => void;
  periodStartPopoverOpen: boolean;
  onPeriodStartPopoverOpenChange: (open: boolean) => void;
  periodEndPopoverOpen: boolean;
  onPeriodEndPopoverOpenChange: (open: boolean) => void;
  hasActiveFilters: boolean;
  onResetFilters: () => void;
  onAddSession: () => void;
};

export function UserSessionsFiltersBar({
  filterType,
  onFilterTypeChange,
  selectedDay,
  onSelectedDayChange,
  dayPopoverOpen,
  onDayPopoverOpenChange,
  selectedMonth,
  onSelectedMonthChange,
  monthPopoverOpen,
  onMonthPopoverOpenChange,
  periodStart,
  periodEnd,
  onPeriodStartChange,
  onPeriodEndChange,
  periodStartPopoverOpen,
  onPeriodStartPopoverOpenChange,
  periodEndPopoverOpen,
  onPeriodEndPopoverOpenChange,
  hasActiveFilters,
  onResetFilters,
  onAddSession,
}: UserSessionsFiltersBarProps) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-2">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Select
            value={filterType}
            onValueChange={(value: UserSessionsFilterType) =>
              onFilterTypeChange(value)
            }
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Filtrer par..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Ce mois</SelectItem>
              <SelectItem value="day">Par jour</SelectItem>
              <SelectItem value="month">Par mois</SelectItem>
              <SelectItem value="period">Par période</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {filterType === "day" && (
            <Popover open={dayPopoverOpen} onOpenChange={onDayPopoverOpenChange}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full gap-2 sm:w-auto">
                  <Calendar className="h-4 w-4 shrink-0" />
                  <span className="truncate">
                    {selectedDay
                      ? format(selectedDay, "dd/MM/yyyy", { locale: fr })
                      : "Choisir un jour"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={selectedDay}
                  onSelect={(day) => {
                    if (day) {
                      onSelectedDayChange(day);
                      onDayPopoverOpenChange(false);
                    }
                  }}
                  className={cn("pointer-events-auto p-3")}
                />
              </PopoverContent>
            </Popover>
          )}

          {filterType === "month" && (
            <Popover
              open={monthPopoverOpen}
              onOpenChange={onMonthPopoverOpenChange}
            >
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full gap-2 sm:w-auto">
                  <Calendar className="h-4 w-4 shrink-0" />
                  <span className="truncate">
                    {selectedMonth
                      ? format(selectedMonth, "MMMM yyyy", { locale: fr })
                      : "Choisir un mois"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={selectedMonth}
                  onSelect={(day) => {
                    if (day) {
                      onSelectedMonthChange(
                        new Date(day.getFullYear(), day.getMonth(), 1)
                      );
                      onMonthPopoverOpenChange(false);
                    }
                  }}
                  className={cn("pointer-events-auto p-3")}
                />
              </PopoverContent>
            </Popover>
          )}

          {filterType === "period" && (
            <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-center">
              <Popover
                open={periodStartPopoverOpen}
                onOpenChange={onPeriodStartPopoverOpenChange}
              >
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full gap-2 sm:w-auto">
                    <Calendar className="h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {periodStart
                        ? format(periodStart, "dd/MM/yyyy", { locale: fr })
                        : "Date début"}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={periodStart}
                    onSelect={(day) => {
                      if (day) {
                        onPeriodStartChange(day);
                        onPeriodStartPopoverOpenChange(false);
                      }
                    }}
                    className={cn("pointer-events-auto p-3")}
                  />
                </PopoverContent>
              </Popover>
              <span className="hidden text-muted-foreground sm:inline">→</span>
              <Popover
                open={periodEndPopoverOpen}
                onOpenChange={onPeriodEndPopoverOpenChange}
              >
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full gap-2 sm:w-auto">
                    <Calendar className="h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {periodEnd
                        ? format(periodEnd, "dd/MM/yyyy", { locale: fr })
                        : "Date fin"}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={periodEnd}
                    onSelect={(day) => {
                      if (day) {
                        onPeriodEndChange(day);
                        onPeriodEndPopoverOpenChange(false);
                      }
                    }}
                    className={cn("pointer-events-auto p-3")}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onResetFilters}
              className="w-full gap-1 sm:w-auto"
            >
              <X className="h-4 w-4" />
              Réinitialiser
            </Button>
          )}
        </div>
      </div>
      <div className="flex w-full justify-end sm:w-auto">
        <Button
          onClick={onAddSession}
          className="w-full gap-2 sm:w-auto"
          size="sm"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Ajouter une session</span>
          <span className="sm:hidden">Ajouter</span>
        </Button>
      </div>
    </div>
  );
}
